import { defineMessages, useIntl } from "react-intl";
import { useMemoryItems } from "../../hooks/useMemory";
import { gridCellExpandRenderer, useDataGridPremium } from "@myplant-io/mui";
import {
  DataGridPremium,
  GridActionsCellItem,
  GridColDef,
  GridOverlay,
  GridRowParams,
  useGridApiRef,
} from "@mui/x-data-grid-premium";
import { useMemo, useState } from "react";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import { MemoryItem } from "../../types";
import { useMemoryDelete } from "../../hooks/useMemoryDelete";
import {
  Box,
  Button,
  IconButton,
  Input,
  InputAdornment,
  Typography,
} from "@mui/material";
import { useDebouncedPickerQuery } from "../../hooks/useDebouncedPickerQuery";
import { commonMessages } from "../../commonMessages";
import { CreateMemoryDialog } from "../../components/common/CreateMemoryDialog";
import { EditMemoryDialog } from "../../components/common/EditMemoryDialog";

const messages = defineMessages({
  header: {
    id: "memory.header",
    defaultMessage: "Memory",
  },
  noRows: {
    id: "memory.noRows",
    defaultMessage: "No memories found",
  },
  number: {
    id: "memory.column.number",
    defaultMessage: "#",
  },
  id: {
    id: "memory.column.id",
    defaultMessage: "ID",
    flex: 1,
  },
  content: {
    id: "memory.column.content",
    defaultMessage: "Content",
  },
  created: {
    id: "memory.column.created",
    defaultMessage: "Created",
  },
  delete: {
    id: "memory.column.delete",
    defaultMessage: "Delete",
  },
  edit: {
    id: "memory.column.edit",
    defaultMessage: "Edit",
  },
  actions: {
    id: "memory.column.actions",
    defaultMessage: "Actions",
  },
  addMemory: {
    id: "memory.addMemory",
    defaultMessage: "Add Memory",
  },
});

export default function Memory() {
  const { formatMessage: t } = useIntl();
  const searchQuery = useDebouncedPickerQuery();
  const { data: allMemories, isLoading } = useMemoryItems();
  const { mutateAsync: deleteMemory } = useMemoryDelete();
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [editingMemory, setEditingMemory] = useState<MemoryItem | null>(null);
  const [isResized, setIsResized] = useState(
    JSON.parse(localStorage.getItem("MEMORY_GRID_STATE_premium") ?? "{}")?.state
      ?.columns?.dimensions?.text?.width > 1
  );

  function CustomNoRowsOverlay() {
    return (
      <GridOverlay>
        <div style={{ padding: "20px", textAlign: "center" }}>
          {t(messages.noRows)}
        </div>
      </GridOverlay>
    );
  }

  const columns: GridColDef<MemoryItem>[] = useMemo(
    () => [
      {
        field: "Nr",
        headerName: t(messages.number),
        width: 70,
        renderCell: (params) =>
          params.api.getAllRowIds().indexOf(params.id) + 1,
      },
      {
        field: "_id",
        headerName: t(messages.id),
        width: 250,
      },
      {
        field: "text",
        headerName: t(messages.content),
        flex: isResized ? 0 : 1,
        renderCell: gridCellExpandRenderer,
      },
      {
        field: "created",
        headerName: t(messages.created),
        width: 300,
        type: "dateTime",
        valueGetter: (value) => (value ? new Date(value) : null),
      },
      {
        field: "actions",
        headerName: t(messages.actions),
        width: 100,
        type: "actions",
        getActions: (params: GridRowParams<MemoryItem>) => [
          <GridActionsCellItem
            key="edit"
            icon={<EditIcon />}
            label={t(messages.edit)}
            onClick={() => {
              setEditingMemory(params.row);
            }}
          />,
          <GridActionsCellItem
            key="delete"
            icon={<DeleteIcon />}
            label={t(messages.delete)}
            onClick={() => {
              deleteMemory(params.id as string);
            }}
          />,
        ],
      },
    ],
    [deleteMemory, isResized, t]
  );

  const apiRef = useGridApiRef();
  const { gridProps, onRestore } = useDataGridPremium({
    apiRef,
    gridStateId: "MEMORY_GRID_STATE",
    columns,
    autoPageSize: true,
    slotProps: {
      toolbar: {
        onRestore: () => {
          setIsResized(false);
          onRestore();
        },
      },
    },
    initialState: {
      sorting: {
        sortModel: [
          {
            field: "created",
            sort: "asc",
          },
        ],
      },
      columns: {
        columnVisibilityModel: {
          _id: false,
        },
      },
    },
  });

  const memories = useMemo(() => {
    return (
      allMemories?.filter(({ text }) =>
        text.toLowerCase().includes(searchQuery.query.toLowerCase())
      ) ?? []
    );
  }, [allMemories, searchQuery.query]);

  return (
    <>
      <div className="p-4 sm:px-6 py-4">
        <Typography variant="h4">{t(messages.header)}</Typography>
      </div>

      <Box className="flex flex-col flex-grow p-4 sm:px-6 py-4">
        <div className="flex flex-col flex-grow w-full" role="main">
          <div className="flex flex-col flex-grow">
            <div className="flex justify-between items-center my-4">
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setIsCreateDialogOpen(true)}
              >
                {t(messages.addMemory)}
              </Button>
              <Input
                data-testid="memory-search"
                id="search"
                placeholder={t(commonMessages.search)}
                value={searchQuery.inputValue}
                autoComplete="off"
                onChange={(e) => searchQuery.setQuery(e.target.value)}
                startAdornment={
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                }
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton
                      disabled={searchQuery.inputValue !== "" ? false : true}
                      aria-label="clear search"
                      onClick={() => searchQuery.setQuery("")}
                      size="large"
                    >
                      <CloseIcon />
                    </IconButton>
                  </InputAdornment>
                }
              />
            </div>
            <div className="flex flex-col flex-1 h-full flex-grow ">
              <DataGridPremium<MemoryItem>
                {...gridProps}
                loading={isLoading}
                rows={memories ?? []}
                disableRowSelectionOnClick
                getRowId={(row) => row._id}
                onColumnResize={(params) => {
                  if (params.colDef.field === "text") {
                    setIsResized(true);
                  }
                }}
                slots={{
                  ...gridProps.slots,
                  noRowsOverlay: CustomNoRowsOverlay,
                }}
                sx={{
                  border: "none",
                  "& .MuiDataGrid-row:hover": {
                    cursor: "pointer",
                  },
                }}
              />
            </div>
          </div>
        </div>
      </Box>
      <CreateMemoryDialog
        open={isCreateDialogOpen}
        onClose={() => setIsCreateDialogOpen(false)}
      />
      <EditMemoryDialog
        open={!!editingMemory}
        memory={editingMemory}
        onClose={() => setEditingMemory(null)}
      />
    </>
  );
}
